import React from 'react';
import { Message } from '../../models';
import { ChatStrings } from '../../i18n';
import ReceptionCard from '../ReceptionCard';
import AnswerBlocks from '../AnswerBlocks';
import AnswerPhotos from '../AnswerPhotos';
import { AlertIcon, LinkOffIcon, PagesIcon, RetryIcon } from '../Icons';

interface MessageItemProps {
   message: Message;
   strings: ChatStrings;
   /** Whether a Retry affordance may be offered for this message right now. */
   canRetry?: boolean;
   onRetry?: (messageId: string) => void;
   /** The answer takes the place of its turn's draft: the same page, already on screen, so it shows without an entrance. */
   inPlace?: boolean;
}

const RetryButton: React.FC<{ label: string; onClick: () => void }> = ({ label, onClick }) => (
   <button type="button" className="cx-btn-small cx-frosted" onClick={onClick}>
      <RetryIcon />
      <span>{label}</span>
   </button>
);

/**
 * One entry of the directory column. A guest message is the question line that introduces a page; an
 * assistant message is the page itself (a white tile), or a failure line, or a notice.
 */
const MessageItem: React.FC<MessageItemProps> = ({ message, strings, canRetry = false, onRetry, inPlace = false }) => {
   const retry = () => message.id && onRetry?.(message.id);

   // ---- the guest's question -------------------------------------------------------------
   if (message.sender === 'user') {
      const isFailed = message.deliveryStatus === 'failed';
      const isPending = message.deliveryStatus === 'pending';
      const text = typeof message.text === 'string' ? message.text : '';

      return (
         <div className={`cx-entry cx-question${isPending ? ' cx-question--pending' : ''}`}>
            <p className="cx-question__text" dir="auto">
               <span className="cx-question__panel">{text}</span>
            </p>
            {isFailed ? (
               <div className="cx-question__meta cx-question__meta--failed" role="alert">
                  <span className="cx-question__note">
                     <AlertIcon />
                     <span>{message.failureKind === 'conflict' ? strings.conflict : strings.notSent}</span>
                  </span>
                  {message.failureKind !== 'conflict' && canRetry && onRetry && <RetryButton label={strings.retry} onClick={retry} />}
               </div>
            ) : (
               <div className="cx-question__meta">
                  <span>{isPending ? strings.sending : message.timestamp}</span>
               </div>
            )}
         </div>
      );
   }

   // ---- the sign-in card, carried as a message so it keeps its slot in the column ----------
   if (message.sender === 'userDetails-bot') {
      return <div className="cx-entry">{React.isValidElement(message.text) ? message.text : null}</div>;
   }

   // ---- something went wrong: an availability problem, never "no information" --------------
   if (message.outcome === 'ProviderError') {
      return (
         <div className="cx-entry cx-failure" role="status">
            <AlertIcon />
            <p className="cx-failure__text">{strings.providerError}</p>
            {canRetry && onRetry && <RetryButton label={strings.retry} onClick={retry} />}
         </div>
      );
   }

   // ---- a notice from the chat itself (an invalid link) ------------------------------------
   if (message.notice) {
      return (
         <div className="cx-entry cx-notice" role="status">
            <LinkOffIcon />
            <p>{typeof message.text === 'string' ? message.text : null}</p>
         </div>
      );
   }

   // ---- the answer: a directory page -------------------------------------------------------
   // The server attaches `receptionContact` exactly when the guest should contact reception (abstained, rejected,
   // or an answered reply that points to reception, e.g. an action request), so its presence drives the panel.
   const showReception =
      !!message.receptionContact || message.outcome === 'Abstained' || message.outcome === 'Rejected' || message.suggestReception === true;
   const sourceCount = message.outcome === 'Answered' ? (message.sources?.length ?? 0) : 0;
   const hasBlocks = !!message.blocks && message.blocks.length > 0;
   const text = typeof message.text === 'string' ? message.text.trim() : '';
   const hasFoot = showReception || sourceCount > 0;
   // The hotel's photos of the places the answer cites (the server sends them only with an answer, never with a draft)
   const photos = message.photos ?? [];

   if (!hasBlocks && !text && !hasFoot && photos.length === 0) return null;

   return (
      <article className={`cx-entry cx-tile cx-frosted cx-frosted--thick${inPlace ? ' cx-entry--in-place' : ''}`}>
         {hasBlocks ? (
            <AnswerBlocks blocks={message.blocks!} />
         ) : text ? (
            <div className="cx-answer">
               {/* Answers without blocks are plain text: never parsed as Markdown or HTML. */}
               <p>{text}</p>
            </div>
         ) : null}

         {/* One gallery after the answer, never between its lines, so the text stays as it was typed */}
         {photos.length > 0 && <AnswerPhotos photos={photos} strings={strings} />}

         {hasFoot && (
            <div className="cx-tile__foot">
               {showReception && <ReceptionCard contact={message.receptionContact} strings={strings} />}
               {sourceCount > 0 && (
                  <p className="cx-sources">
                     <PagesIcon />
                     <span>{strings.sources(sourceCount)}</span>
                  </p>
               )}
            </div>
         )}
      </article>
   );
};

export default MessageItem;
